import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, TrendingUp, Heart, ArrowLeft, Check,
  BarChart3, Shield, Headphones, Globe, Send, Loader2
} from "lucide-react";
import { toast } from "sonner";

export default function CorporatePage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", employee_count: 50, message: "" });

  useEffect(() => {
    api().get("/corporate/stats").then(r => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api().post("/corporate/inquiry", form);
      toast.success("Érdeklődés elküldve! Hamarosan felvesszük a kapcsolatot.");
      setForm({ company_name: "", contact_name: "", email: "", employee_count: 50, message: "" });
    } catch {
      toast.error("Hiba az érdeklődés elküldésénél");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const features = [
    { icon: BarChart3, title: "Csapat Dashboard", desc: "Valós idejű egészségügyi mutatók az egész csapatról" },
    { icon: Users, title: "Csapat Kihívások", desc: "Motiváló kihívások és versenyek a csapatok között" },
    { icon: Heart, title: "Wellness Program", desc: "Személyre szabott wellness programok munkavállalóknak" },
    { icon: Shield, title: "Adatbiztonság", desc: "GDPR kompatibilis, titkosított adattárolás" },
    { icon: Headphones, title: "Dedikált Támogatás", desc: "Account manager és prioritásos ügyfélszolgálat" },
    { icon: Globe, title: "API Integráció", desc: "HR rendszerek és belső platformok összekapcsolása" },
  ];

  return (
    <div className="space-y-6 md:pt-20 max-w-4xl mx-auto" data-testid="corporate-page">
      <div className="flex items-center gap-4 fade-in-up">
        <button
          data-testid="corporate-back-btn"
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <ArrowLeft size={18} className="text-white" />
        </button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Corporate Wellness</h1>
          <p className="text-sm text-gray-400">Vállalati egészségügyi megoldások</p>
        </div>
        <Building2 className="text-[#00f260] ml-auto" size={24} />
      </div>

      {/* Stats Hero */}
      <GlassCard glow className="fade-in-up fade-in-up-1" testId="corporate-stats">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="font-heading text-3xl font-bold text-[#00f260]">{stats?.active_companies}+</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Aktív vállalat</p>
          </div>
          <div>
            <p className="font-heading text-3xl font-bold text-white">{(stats?.total_employees || 0).toLocaleString()}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Munkavállaló</p>
          </div>
          <div>
            <p className="font-heading text-3xl font-bold text-[#00f260]">{stats?.avg_engagement}%</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Elköteleződés</p>
          </div>
          <div>
            <p className="font-heading text-3xl font-bold text-white">+{stats?.health_improvement}%</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Egészség javulás</p>
          </div>
        </div>
      </GlassCard>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <GlassCard key={f.title} className={`fade-in-up fade-in-up-${i + 2}`} testId={`feature-${i}`}>
              <Icon size={22} className="text-[#00f260] mb-3" />
              <h3 className="text-sm text-white font-semibold mb-1">{f.title}</h3>
              <p className="text-xs text-gray-400">{f.desc}</p>
            </GlassCard>
          );
        })}
      </div>

      {/* Pricing Plans */}
      <div className="space-y-3">
        <p className="text-xs text-gray-400 uppercase tracking-wider fade-in-up">Csomagok</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats?.plans?.map((plan, i) => (
            <GlassCard
              key={plan.name}
              className={`fade-in-up fade-in-up-${i + 3} ${i === 1 ? "border-[#00f260]/30 shadow-[0_0_20px_rgba(0,242,96,0.1)]" : ""}`}
              testId={`plan-${i}`}
            >
              <div className="text-center mb-4">
                {i === 1 && <Badge className="bg-[#00f260] text-black border-0 text-[10px] mb-2">Népszerű</Badge>}
                <h3 className="font-heading text-xl font-bold text-white">{plan.name}</h3>
                <p className="text-lg font-semibold text-[#00f260] mt-1">{plan.price}</p>
                <p className="text-xs text-gray-400">{plan.employees}</p>
              </div>
              <div className="space-y-2">
                {plan.features?.map((feature, fi) => (
                  <div key={fi} className="flex items-center gap-2">
                    <Check size={14} className="text-[#00f260]" />
                    <span className="text-xs text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </GlassCard>
          ))}
        </div>
      </div>

      {/* Contact Form */}
      <GlassCard glow className="fade-in-up" testId="corporate-inquiry-form">
        <h3 className="font-heading text-xl font-bold text-white mb-4">Érdeklődés</h3>
        <p className="text-sm text-gray-400 mb-6">Töltsd ki az űrlapot és csapatunk felveszi veled a kapcsolatot 24 órán belül.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Cégnév</Label>
              <Input
                data-testid="corporate-company-input"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Kapcsolattartó neve</Label>
              <Input
                data-testid="corporate-contact-input"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Email</Label>
              <Input
                data-testid="corporate-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Létszám</Label>
              <Input
                data-testid="corporate-employees-input"
                type="number"
                value={form.employee_count}
                onChange={(e) => setForm({ ...form, employee_count: parseInt(e.target.value) || 0 })}
                className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Üzenet (opcionális)</Label>
            <textarea
              data-testid="corporate-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              className="w-full bg-black/20 border border-white/10 text-white rounded-xl p-3 text-sm focus:border-[#00f260] focus:ring-[#00f260] focus:outline-none resize-none"
            />
          </div>
          <button
            data-testid="corporate-submit-btn"
            type="submit"
            disabled={sending}
            className="neon-btn w-full text-sm flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Érdeklődés elküldése
          </button>
        </form>
      </GlassCard>
    </div>
  );
}
